import Box from "@mui/material/Box";
import BasicModal from "@/components/basic-modal";
import { useFormik } from "formik";
import UITextField from "@/components/ui-controls/textfield";
import UISelect from "@/components/ui-controls/select";
import GeneralSkeleton from "@/components/skeleton/general-skeleton";
import {
  AppointmentInitialValues,
  AppointmentSchema,
} from "@/modules/crm/appointment/schema/appointment-schema";
import { Collapse, Typography } from "@mui/material";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import {
  faLocationDot,
  faPhone,
  faLink,
  faCommentDots,
} from "@fortawesome/free-solid-svg-icons";
import GetIcon, { GetFontAwesomeIcon } from "@/components/font-awesome-icon";
import { LocationType, Enums } from "@/services/dropdown-enums";

import { useSelector, useDispatch } from "react-redux";
import { getTimezones } from "@/store/slices/general/generalSlice";
import LocationFields from "./location-fields";
import {
  autoSelectPublicLocation,
  getActiveAppointmentLocations,
  shouldShowPublicLocationSelector,
  validatePublicLocationFields,
} from "@/components/public/BookAnAppointment/utils/location-helpers";
import DateFields from "./date-fields";
import {
  getCalendars,
  getCalendarsCallState,
  getUnsavedFormData,
  getUnsavedLeadFormData,
  setUnsavedFormData,
  clearFormDraft,
  setUnsavedLeadFormData,
  clearLeadFormDraft,
  getSavedInfoWhenLeadCreate,
  setSavedInfoWhenLeadCreate,
  getPendingAppointmentModal,
  getAppointmentModalShowNewLeadForm,
  setPendingAppointmentModal,
  setAppointmentModalShowNewLeadForm,
  clearPendingAppointmentModalState,
  getAppointmentInlineLeadDraft,
  setAppointmentInlineLeadDraft,
  clearAppointmentInlineLeadDraft,
} from "@/store/slices/appointment/appointmentSlice";
import { setDraftLeadEdit } from "@/store/slices/lead/leadSlice";
import { useCallback, useEffect, useRef, useState } from "react";

import useAuthProvider from "@/modules/crm/settings/hooks/useAuthProvider";
import SearchLead, { getPrefillFromSearchInput } from "./search-lead";
import useManageAppointment from "./useManageAppointment"; // import the custom hook
import TimeZoneField from "./timezone-field";
import { useRouter } from "next/router";
import UIButton from "@/components/ui-controls/button";

import { getCurrentTimezone } from "@/services/generic.methods";
import { toast } from "react-toastify";
import { ToastTemplate } from "@/services/variables";
import trackingService from "@/services/tracking/tracking-service";
import { TRACKING_EVENTS } from "@/services/tracking/tracking-events";
import DynamicForm from "@/components/dynamic-renderer/dynamic-form";
import Link from "@mui/material/Link";
import { newLeadFormJson } from "./helper";
import useManageLead from "@/hooks/leads/useManageLead";
import Lead_signalr from "@/modules/crm/leads/signalr-constants";
import moment from "moment";
import { time } from "@/modules/crm/global/constants";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import { Get_Leads_Grid } from "@/modules/crm/leads/graphql-queries";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
const {
  InvokeMethodNames: { addLead },
  onReceiveMethodNames: { onAddLead },
} = Lead_signalr;
const APPOINTMENT_LEAD_DRAFT_KEY = "new";

const unwrapLeadResponse = (res) => {
  if (!res) return null;
  if (res.firstName || res.id || res.leadId || res.email) return res;
  return res.leadDetails?.[0] || res.leadDetails || res.leadsGrid?.items?.[0] || null;
};

const leadFromAppointmentGuest = (data = {}, leadId) => {
  const name = (data.visitorName || data.name || data.guest || "").trim();
  if (!leadId && !name) return null;
  const [firstName, ...rest] = name.split(/\s+/);
  return {
    id: leadId || undefined,
    leadId: leadId || undefined,
    firstName: firstName || name,
    lastName: rest.join(" "),
    email: data.visitorEmail || data.email || "",
  };
};

const AppointmentModal = ({
  open,
  onClose,
  title = "Appointment",
  onSave = () => {},
  // loading,
  data,
  size = "sm",
  lead = {},
  requestType = "",
  handleAppointmentStatus = () => {},
  statusLoading = false,
  showResponse = true,
  showStatusResponse = false,
  range,
  returnFrom = "",
  isReturningFromLead = false,
}) => {
  const { fetchCalendars, calendarLoading } = useAuthProvider({
    authType: "Calendar",
  });
  const timezones = useSelector(getTimezones);
  const [calendarOptions, setCalendarOptions] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [guests, setGuests] = useState([]);
  const [tempLead, setTemplead] = useState(null);
  const calendars = useSelector(getCalendars);
  const isCalendarsFetched = useSelector(getCalendarsCallState);
  const unsavedFormData = useSelector(getUnsavedFormData); // For appointments tab
  const unsavedLeadFormData = useSelector(getUnsavedLeadFormData); // For lead details
  const savedInfoWhenLeadCreate = useSelector(getSavedInfoWhenLeadCreate);
  const pendingAppointmentModal = useSelector(getPendingAppointmentModal);
  const appointmentModalShowNewLeadForm = useSelector(
    getAppointmentModalShowNewLeadForm
  );
  const appointmentInlineLeadDraft = useSelector(getAppointmentInlineLeadDraft);
  const inlineLeadDraftRef = useRef(appointmentInlineLeadDraft);
  const router = useRouter();
  const dispatch = useDispatch();

  const clearPendingModalFlags = () => {
    dispatch(clearPendingAppointmentModalState());
  };

  const clearInlineLeadDraft = () => {
    dispatch(clearAppointmentInlineLeadDraft());
    inlineLeadDraftRef.current = null;
  };

  const hasInlineLeadDraftContent = (draft) =>
    draft &&
    Object.values(draft).some((v) => v != null && String(v).trim() !== "");

  const shouldPersistInlineLeadForm = (
    inlineDraft,
    existingDraft,
    showForm = showNewLeadForm
  ) =>
    showForm ||
    pendingAppointmentModal ||
    existingDraft?.showInlineLeadForm ||
    hasInlineLeadDraftContent(inlineDraft);

  const restoreInlineLeadFormFromDraft = (draft) => {
    if (!draft) return;
    if (draft.inlineLeadDraft) {
      dispatch(setAppointmentInlineLeadDraft(draft.inlineLeadDraft));
      inlineLeadDraftRef.current = draft.inlineLeadDraft;
    }
    if (shouldPersistInlineLeadForm(draft.inlineLeadDraft, draft)) {
      setShowNewLeadForm(true);
    }
  };

  const handleInlineLeadValuesChange = useCallback(
    (values) => {
      inlineLeadDraftRef.current = values;
      dispatch(setAppointmentInlineLeadDraft(values));
    },
    [dispatch]
  );

  useEffect(() => {
    inlineLeadDraftRef.current = appointmentInlineLeadDraft;
  }, [appointmentInlineLeadDraft]);
  const [inValidLead, setInvalidLead] = useState(false);
  const [isSlotDisabled, setIsSlotDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewLeadForm, setShowNewLeadForm] = useState(false);
  const [leadSaveLoading, setLeadSaveLoading] = useState(
    Enums.RequestStatus.initial
  );
  const isLeadSaving = leadSaveLoading === Enums.RequestStatus.processing;
  const [selectedCalendar, setSelectedCalendar] = useState(null);

  const { client: dataClient } = useCRMDataClient();
  const user = useSelector(getLoggedinUser);
  const isSchedulingUser =
    user?.RoleType === Enums.Roles.SchedulingUser ||
    user?.RoleType === Enums.Roles.SchedulingTeamManager;

  const handleFormikSubmit = (formData) => {
    handleFormSubmit(
      AppointmentForm,
      formData,
      data,
      requestType,
      onSave,
      tempLead || lead
    );
  };

  const AppointmentForm = useFormik({
    enableReinitialize: true,
    initialValues: AppointmentInitialValues,
    validationSchema: AppointmentSchema,
    validate: (values) =>
      validatePublicLocationFields(
        values,
        selectedCalendar?.appointmentLocations || []
      ),
    onSubmit: handleFormikSubmit,
  });

  const resolveLeadByEmail = async (email) => {
    try {
      const res = await dataClient.leads.getLeadByEmail(email, {
        query: Get_Leads_Grid,
        companyKey: user?.CompanyKey,
      });
      const resolved = unwrapLeadResponse(res);
      if (resolved) {
        const resolvedId = resolved.id || resolved.leadId;
        AppointmentForm.setFieldValue("leadId", resolvedId);
        setTemplead({
          ...resolved,
          id: resolvedId,
          leadId: resolvedId,
          email: resolved.email || email,
        });
      }
    } catch (error) {
      console.error("Error resolving lead by email in modal:", error);
    }
  };

  const handleSuccess = (response) => {
    const submittedDraft = inlineLeadDraftRef.current;
    setLeadSaveLoading(Enums.RequestStatus.initial);
    setShowNewLeadForm(false);
    const lead = response?.lead ?? response;
    const contact = lead?.contact || {};
    const leadId = lead?.id || lead?.leadId;
    setTemplead({
      ...lead,
      id: leadId,
      leadId,
      firstName:
        lead?.firstName || contact.firstName || submittedDraft?.firstName || "",
      lastName:
        lead?.lastName || contact.lastName || submittedDraft?.lastName || "",
      email:
        lead?.email ||
        contact.email ||
        submittedDraft?.email ||
        response?.email ||
        "",
    });
    clearInlineLeadDraft();
    AppointmentForm.setFieldValue("leadId", leadId);
    setInvalidLead(false);
  };

  const { handleSubmit } = useManageLead({
    requestMethod: addLead,
    responseMethod: onAddLead,
    successMsg: "Lead created successfully.",
    shouldShowToast: true,
    onSaveSuccess: handleSuccess,
  });

  const handleModalClose = (shouldSaveDraft = false) => {
    setIsSubmitting(false);
    setLeadSaveLoading(Enums.RequestStatus.initial);
    const isNavigatingToLeadCreate = pendingAppointmentModal;
    // Only save draft for NEW appointment creation, not when editing existing ones
    if (shouldSaveDraft && !data?.id) {
      const currentValues = AppointmentForm.values;
      const inlineDraft =
        inlineLeadDraftRef.current || appointmentInlineLeadDraft;
      const existingDraft = lead?.id ? unsavedLeadFormData : unsavedFormData;
      const hasInlineDraft = hasInlineLeadDraftContent(inlineDraft);
      const hasData =
        showNewLeadForm ||
        hasInlineDraft ||
        currentValues.title ||
        currentValues.notes ||
        currentValues.leadId ||
        currentValues.date ||
        (currentValues.calendarId && currentValues.calendarId !== 0);
      if (hasData) {
        const draftPayload = {
          formValues: currentValues,
          savedLead: tempLead,
          ...(shouldPersistInlineLeadForm(inlineDraft, existingDraft) && {
            showInlineLeadForm: true,
            inlineLeadDraft: inlineDraft,
          }),
        };
        if (lead?.id) {
          dispatch(
            setUnsavedLeadFormData({
              leadId: lead.id,
              ...draftPayload,
            })
          );
        } else {
          dispatch(setUnsavedFormData(draftPayload));
        }
      }
    } else if (!isNavigatingToLeadCreate) {
      if (lead?.id) {
        dispatch(clearLeadFormDraft());
      } else {
        dispatch(clearFormDraft());
      }
      dispatch(setSavedInfoWhenLeadCreate(null));
      clearPendingModalFlags();
      clearInlineLeadDraft();
    }
    AppointmentForm.resetForm({ values: AppointmentInitialValues });
    setTemplead(null);
    if (lead && Object.keys(lead).length) {
      AppointmentForm.setFieldValue("leadId", lead?.id || "");
    }
    onClose();
    setReadOnlyFields({});
    setLoading(false);
  };

  const {
    loading,
    handleFormSubmit,
    setLoading,
    offsetMinutes,
    updateOffsetFromTimezone,
  } = useManageAppointment({
    handleModalClose,
    fetchCalendars,
    showResponse,
    showStatusResponse,
    range,
  });

  useEffect(() => {
    if (!isCalendarsFetched) {
      fetchCalendars();
    }
  }, [isCalendarsFetched]);

  useEffect(() => {
    if (calendars?.length) {
      setCalendarOptions(
        calendars?.map((mem) => {
          return {
            label: `${mem?.name || ""}`,
            value:
              mem?.participantId || mem?.uuid || mem?.calendarId || mem?.id,
          };
        })
      );
    }
  }, [calendars]);

  useEffect(() => {
    let isMounted = true;

    const fetchSelectedCalendar = async () => {
      const calendarId = AppointmentForm?.values?.calendarId;
      if (calendars?.length && calendarId) {
        try {
          const response = await dataClient.appointments.getCalendarById({
            id: calendarId,
          });
          if (isMounted && response?.calendar) {
            setSelectedCalendar(response.calendar);
          }
        } catch (error) {
          console.error("Failed to fetch selected calendar details:", error);
        }
      } else {
        if (isMounted) setSelectedCalendar(null);
      }
    };

    fetchSelectedCalendar();

    return () => {
      isMounted = false;
    };
  }, [AppointmentForm?.values?.calendarId, calendars, dataClient]);

  useEffect(() => {
    if (data?.id || data?.eventId || data?.appointmentId) return;

    const locations = selectedCalendar?.appointmentLocations || [];
    const active = getActiveAppointmentLocations(locations);

    if (active.length === 1) {
      autoSelectPublicLocation(
        locations,
        AppointmentForm.values.calendarLocationId,
        (key, value) => AppointmentForm.setFieldValue(key, value)
      );
    } else {
      AppointmentForm.setFieldValue("calendarLocationId", "");
      AppointmentForm.setFieldValue("customLocation", "");
    }
  }, [selectedCalendar, data]);

  const [readOnlyFields, setReadOnlyFields] = useState({});
  const [saveButtonTitle, setSaveButtonTitle] = useState("Create");
  useEffect(() => {
    if (data?.id || data?.eventId || data?.appointmentId) {
      setSaveButtonTitle("Reschedule");
    }

    if (requestType === "view") {
      setReadOnlyFields({
        calendarId: true,
        title: true,
        leadId: true,
        notes: true,
        date: true,
        calendarLocationId: true,
        customLocation: true,
      });
    } else if (requestType == "reschedule") {
      setReadOnlyFields({
        calendarId: true,
        title: true,
        leadId: true,
      });
      setSaveButtonTitle("Reschedule");
    } else if (requestType == "update") {
      setReadOnlyFields({
        calendarId: true,
        leadId: true,
        date: true,
      });
      setSaveButtonTitle("Update");
    } else if (!data?.id && !data?.eventId && !data?.appointmentId) {
      setReadOnlyFields({});
      setSaveButtonTitle("Create");
    }
  }, [requestType, data]);

  const onOpenModal = () => {
    const shouldRestoreFromSaved =
      !!savedInfoWhenLeadCreate &&
      (pendingAppointmentModal || returnFrom === "lead" || isReturningFromLead);

    if (shouldRestoreFromSaved) {
      const unsavedDraft = lead?.id ? unsavedLeadFormData : unsavedFormData;
      restoreInlineLeadFormFromDraft(unsavedDraft);

      const currentTz = getCurrentTimezone(timezones);
      const draftFormValues = unsavedDraft?.formValues || {};
      const restoredLead =
        unsavedDraft?.savedLead ||
        (savedInfoWhenLeadCreate?.leadId
          ? { id: savedInfoWhenLeadCreate.leadId }
          : null);

      const restoredValues = {
        ...AppointmentForm.values,
        timeZone: currentTz,
        ...draftFormValues,
        ...savedInfoWhenLeadCreate,
      };
      AppointmentForm.setValues(restoredValues);
      updateOffsetFromTimezone(restoredValues.timeZone || currentTz);

      if (restoredLead) {
        setTemplead(restoredLead);
      }

      if (restoredValues.calendarId) {
        setIsCollapsed(true);
      }

      const shouldShowInlineLeadForm =
        !savedInfoWhenLeadCreate?.leadId &&
        (appointmentModalShowNewLeadForm ||
          shouldPersistInlineLeadForm(
            unsavedDraft?.inlineLeadDraft,
            unsavedDraft
          ));
      setShowNewLeadForm(shouldShowInlineLeadForm);
      clearPendingModalFlags();
      dispatch(setSavedInfoWhenLeadCreate(null));
      return;
    }

    if (data && Object.keys(data).length) {
      const currentTz = getCurrentTimezone(timezones);
      const notes = data?.notes || data?.description || "";
      const tz = data?.timeZone || data?.selectedTimeZone || currentTz;
      const leadId = data?.leadId || lead?.id || "";
      const contextLeadHasName = !!(lead?.firstName || lead?.lastName);
      const contextLeadMatches =
        !!lead?.id && !!leadId && String(lead.id) === String(leadId);
      const email = data?.visitorEmail || data?.email || lead?.email;

      if (contextLeadHasName && (contextLeadMatches || !leadId)) {
        setTemplead({
          ...lead,
          id: lead.id || leadId,
          email: lead.email || email || "",
        });
      } else {
        const guestLead = leadFromAppointmentGuest(data, leadId);
        if (guestLead?.firstName) {
          setTemplead(guestLead);
        }
        if (email) {
          resolveLeadByEmail(email);
        }
      }

      let mappedCalendarLocationId = data?.calendarLocationId;
      if (mappedCalendarLocationId === null && data?.customLocation) {
        mappedCalendarLocationId = 3;
      }

      let savedData = {
        ...data,
        calendarLocationId: mappedCalendarLocationId,
        notes,
        timeZone: tz,
        leadId,
        // Handle date from calendar slot click (data.date is a moment object)
        // or from existing appointment (data.startDate is ISO string)
        date: data?.date
          ? moment(data.date).startOf("day").format(time.format.isoStringFormat)
          : data?.startDate
            ? moment
                .utc(data.startDate)
                .startOf("day")
                .format(time.format.isoStringFormat)
            : undefined,
      };

      if (savedData.title === "") {
        delete savedData.title; // don't override draft title with an empty string
      }
      if (!notes) {
        delete savedData.notes;
      }
      if (savedData.date === undefined) {
        delete savedData.date;
      }

      // Use lead-specific draft if available, otherwise no draft for editing
      let draftValues = {};
      let draftLead = null;
      if (
        lead?.id &&
        unsavedLeadFormData &&
        unsavedLeadFormData.leadId === lead.id &&
        !data?.id &&
        !requestType
      ) {
        draftValues = unsavedLeadFormData.formValues || {};
        draftLead = unsavedLeadFormData.savedLead;
        restoreInlineLeadFormFromDraft(unsavedLeadFormData);
      } else if (!lead?.id && unsavedFormData && !data?.id && !requestType) {
        draftValues = unsavedFormData.formValues || {};
        draftLead = unsavedFormData.savedLead;
        restoreInlineLeadFormFromDraft(unsavedFormData);
      }
      if (draftLead) {
        setTemplead(draftLead);
      }

      AppointmentForm.setValues({
        ...AppointmentForm.values,
        timeZone: currentTz,
        ...savedData,
        ...draftValues,
        ...(savedInfoWhenLeadCreate || {}),
      });
      updateOffsetFromTimezone(tz);
    } else if (lead?.id && unsavedLeadFormData && !requestType) {
      // When opening from lead details - only restore if draft belongs to this lead
      if (unsavedLeadFormData.leadId === lead.id) {
        const currentTz = getCurrentTimezone(timezones);
        const { formValues, savedLead } = unsavedLeadFormData;
        if (formValues) {
          AppointmentForm.setValues({
            ...AppointmentForm.values,
            timeZone: currentTz,
            ...formValues,
          });
        }
        if (savedLead) {
          setTemplead(savedLead);
        }
        restoreInlineLeadFormFromDraft(unsavedLeadFormData);
      }
      // If leadId doesn't match, start fresh (data is lost when switching leads)
    } else if (!lead?.id && unsavedFormData && !requestType) {
      // When opening from appointments tab - restore general draft
      const currentTz = getCurrentTimezone(timezones);
      const { formValues, savedLead } = unsavedFormData;
      if (formValues) {
        AppointmentForm.setValues({
          ...AppointmentForm.values,
          timeZone: currentTz,
          ...formValues,
          ...(savedInfoWhenLeadCreate || {}),
        });
      }
      if (savedLead) {
        setTemplead(savedLead);
      } else {
        if (savedInfoWhenLeadCreate?.leadId) {
          setTemplead({ id: savedInfoWhenLeadCreate?.leadId });
        }
      }
      restoreInlineLeadFormFromDraft(unsavedFormData);
    } else {
      const currentTz = getCurrentTimezone(timezones);
      AppointmentForm.setFieldValue("timeZone", currentTz);
    }

    if (savedInfoWhenLeadCreate?.leadId) {
      AppointmentForm.setFieldValue(
        "leadId",
        savedInfoWhenLeadCreate?.leadId || ""
      );
      setTemplead({ id: savedInfoWhenLeadCreate?.leadId });
    }
  };

  useEffect(() => {
    if (!open) return;
    if (
      pendingAppointmentModal ||
      isReturningFromLead ||
      returnFrom === "lead"
    ) {
      return;
    }
    if (AppointmentForm.values?.leadId || tempLead?.id) {
      setShowNewLeadForm(false);
    }
  }, [
    open,
    AppointmentForm.values?.leadId,
    tempLead?.id,
    pendingAppointmentModal,
    isReturningFromLead,
    returnFrom,
  ]);

  // Restore inline lead form when modal reopens (backup for onEnter timing)
  useEffect(() => {
    if (!open || data?.id || requestType) return;
    const draft = lead?.id ? unsavedLeadFormData : unsavedFormData;
    if (!draft) return;
    if (lead?.id && draft.leadId !== lead.id) return;
    restoreInlineLeadFormFromDraft(draft);
  }, [open]);

  // Auto-save draft changes to preserve state when switching tabs/unmounting
  useEffect(() => {
    if (!open || isSubmitting || pendingAppointmentModal) return;
    const timer = setTimeout(() => {
      if (!data?.id && !requestType) {
        const currentValues = AppointmentForm.values;
        const inlineDraft =
          inlineLeadDraftRef.current || appointmentInlineLeadDraft;
        const existingDraft = lead?.id ? unsavedLeadFormData : unsavedFormData;
        const hasInlineDraft = hasInlineLeadDraftContent(inlineDraft);
        const hasData =
          showNewLeadForm ||
          hasInlineDraft ||
          currentValues.title ||
          currentValues.notes ||
          currentValues.leadId ||
          currentValues.date ||
          (currentValues.calendarId && currentValues.calendarId !== 0);

        if (hasData) {
          const draftPayload = {
            formValues: currentValues,
            savedLead: tempLead,
            ...(shouldPersistInlineLeadForm(inlineDraft, existingDraft) && {
              showInlineLeadForm: true,
              inlineLeadDraft: inlineDraft,
            }),
          };
          if (lead?.id) {
            dispatch(
              setUnsavedLeadFormData({
                leadId: lead.id,
                ...draftPayload,
              })
            );
          } else {
            dispatch(setUnsavedFormData(draftPayload));
          }
        }
      }
    }, 500);
    return () => clearTimeout(timer);
    // Use primitive values to avoid infinite re-renders from object reference changes
  }, [
    AppointmentForm.values.title,
    AppointmentForm.values.notes,
    AppointmentForm.values.leadId,
    AppointmentForm.values.date,
    AppointmentForm.values.calendarId,
    tempLead?.id,
    open,
    data?.id,
    requestType,
    lead?.id,
    isSubmitting,
    showNewLeadForm,
    appointmentInlineLeadDraft,
    pendingAppointmentModal,
    unsavedFormData?.showInlineLeadForm,
    unsavedLeadFormData?.showInlineLeadForm,
  ]);

  useEffect(() => {
    const currentTz = getCurrentTimezone(timezones);
    if (!data?.id && !data?.eventId && !data?.appointmentId) {
      AppointmentForm.setFieldValue("timeZone", currentTz);
      updateOffsetFromTimezone(currentTz);
    }
    if (!open) {
      setIsCollapsed(false);
    }
  }, [open]);

  const showInvalidLeadError = () => {
    toast.dismiss();
    toast.error(
      ToastTemplate.error(
        "Cannot proceed with booking: Email address is missing for this lead."
      )
    );
  };

  useEffect(() => {
    // Only validate when modal is open
    if (!open) return;

    if (tempLead && Object.keys(tempLead).length !== 1) {
      const leadId = tempLead?.id || tempLead?.leadId;
      if (!leadId) {
        setInvalidLead(true);
      } else {
        setInvalidLead(false);
      }
    }
  }, [tempLead, open]);

  useEffect(() => {
    // Only show error toast when modal is open
    if (!open) return;

    if (inValidLead) {
      showInvalidLeadError();
    }
  }, [inValidLead, open]);

  const onChange = (key, value, tempLead) => {
    console.log("change", key, value, tempLead);

    AppointmentForm.setFieldValue(key, value);
    if (key == "timeZone") {
      updateOffsetFromTimezone(value);
    }
    if (key == "leadId") {
      AppointmentForm.setFieldValue("leadId", value);
      if (tempLead) {
        setTemplead(tempLead);
      }
    }
  };

  const handleCalendarChange = async (e) => {
    const calendarId = e.target.value;

    // Update the field immediately so the UI reflects selection without waiting for the API response
    AppointmentForm.handleChange(e);
    if (!isCollapsed && calendarId) {
      setIsCollapsed(true);
    }

    if (calendarId) {
      try {
        const authRes =
          await dataClient.appointments.checkAuthorizationByCalendar(
            calendarId
          );
        const isAvailable =
          Array.isArray(authRes) && authRes.some((p) => p.isApproved);
        if (!isAvailable) {
          toast.error(
            "No participant available to create appointment with this appointment type"
          );
          AppointmentForm.setFieldValue("calendarId", "");
        }
      } catch (error) {
        console.error("Failed to check authorization:", error);
      }
    }
  };

  const [isCalendarDisabled, setIsCalendarDisabled] = useState(true);
  const [isRerendering, setIsRerendering] = useState(false);

  useEffect(() => {
    setIsCalendarDisabled(
      !(
        AppointmentForm?.values?.timeZone && AppointmentForm?.values?.calendarId
      )
    );
    setIsRerendering(true);
    setTimeout(() => {
      setIsRerendering(false);
    }, 200);
    if (AppointmentForm?.values?.calendarId) {
      setIsCollapsed(true);
    }
  }, [AppointmentForm?.values?.timeZone, AppointmentForm?.values?.calendarId]);

  const handleAppointmentSave = () => {
    console.log("Appointment Form Values:", AppointmentForm.values);
    console.log("Appointment Form Validation Errors:", AppointmentForm.errors);
    // if(isCalendarsFetched && !calendarOptions?.length) {
    //   router.push("/crm/settings/integrations/appointment-scheduling")
    // } else {

    if (inValidLead) {
      showInvalidLeadError();
      return;
    }
    if (!data?.id) {
      trackingService.track(TRACKING_EVENTS.CREATE_APPOINTMENT);
    }
    AppointmentForm.handleSubmit();
    // }
  };

  const isCanceledAppointment = (apptData) => {
    const statusVal = String(apptData?.status || "").toLowerCase();
    return (
      apptData?.status === Enums.AppointmentStates.Canceled ||
      statusVal === "canceled" ||
      statusVal === "cancelled" ||
      statusVal === "5"
    );
  };

  const isCompletedAppointment = (apptData) => {
    const statusVal = String(apptData?.status || "").toLowerCase();
    return (
      apptData?.status === Enums.AppointmentStates.Completed ||
      statusVal === "complete" ||
      statusVal === "completed" ||
      statusVal === "0"
    );
  };

  const handleStatus = () => {
    if (
      data?.id &&
      !isCanceledAppointment(data) &&
      !isCompletedAppointment(data)
    ) {
      handleAppointmentStatus(data?.id, Enums.AppointmentStates.Canceled);
    }
    // Clear the appropriate draft based on context
    if (lead?.id) {
      dispatch(clearLeadFormDraft());
    } else {
      dispatch(clearFormDraft());
    }
    dispatch(setSavedInfoWhenLeadCreate(null));
    clearPendingModalFlags();
    clearInlineLeadDraft();
    setTemplead(null);
    handleModalClose(false);
  };
  const getSecondaryBtnText = () => {
    if (
      data?.id &&
      !isCanceledAppointment(data) &&
      !isCompletedAppointment(data)
    ) {
      return "Cancel Appointment";
    }
    return "Close";
  };

  const handleNewLeadSubmit = (data) => {
    if (isLeadSaving) {
      return;
    }
    if (!data?.email?.trim() || !data?.firstName?.trim()) {
      return;
    }
    const params = {
      data,
      setLoading: setLeadSaveLoading,
      redirect: false,
      requireEmailAndFirstName: true,
    };
    handleSubmit(params);
  };

  const inlineLeadDraftForForm =
    appointmentInlineLeadDraft ||
    unsavedFormData?.inlineLeadDraft ||
    unsavedLeadFormData?.inlineLeadDraft ||
    null;

  const handleSearchLeadCreateNew = (searchText = "") => {
    const prefill = getPrefillFromSearchInput(searchText);
    if (Object.keys(prefill).length > 0) {
      dispatch(setAppointmentInlineLeadDraft(prefill));
      inlineLeadDraftRef.current = prefill;
    } else {
      clearInlineLeadDraft();
    }
    setShowNewLeadForm(true);
  };

  const CustomFooterButtons = () => {
    return (
      <>
        <UIButton
          title={getSecondaryBtnText()}
          onClick={handleStatus}
          variant={"outlined"}
          sx={{ padding: "2px 10px" }}
          loading={statusLoading}
        />
        <UIButton
          title={saveButtonTitle}
          onClick={handleAppointmentSave}
          sx={{ padding: "2px 10px" }}
          loading={loading}
        />
      </>
    );
  };

  const getLabelByType = (type) => {
    switch (type) {
      case LocationType.Physical:
        return "In Person";
      case LocationType.Video:
        return "Custom Link";
      case LocationType.Phone:
        return "Phone Call";
      case LocationType.Custom:
        return "Ask Invitee";
      default:
        return "Location";
    }
  };

  const getIconByLocationType = (type) => {
    switch (type) {
      case LocationType.Physical:
        return faLocationDot;
      case LocationType.Video:
        return faLink;
      case LocationType.Phone:
        return faPhone;
      case LocationType.Custom:
        return faCommentDots;
      default:
        return faLocationDot;
    }
  };

  const predefinedLocations = (selectedCalendar?.appointmentLocations || [])
    .filter((loc) => loc.type !== LocationType.Custom) // Exclude Ask Invitee (Type 3)
    .map((loc) => {
      const icon = getIconByLocationType(loc.type);
      return {
        value: loc.value,
        tooltipTitle:
          typeof loc.value === "string"
            ? loc.value
            : loc.value?.text || getLabelByType(loc.type) || "",
        label: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1,
              mt: 1,
              minWidth: 0,
              width: "100%",
              overflow: "hidden",
            }}
          >
            <GetFontAwesomeIcon
              icon={icon}
              iconStyles={{
                fontSize: "14px",
                marginRight: 0,
                color: "var(--palette-secondary_button_text)",
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                minWidth: 0,
                flex: 1,
                overflow: "hidden",
              }}
            >
              {loc.type === LocationType.Phone && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    lineHeight: 1,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getLabelByType(loc.type)}
                </Typography>
              )}
              {loc.type !== LocationType.Phone && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    lineHeight: 1.2,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                  title={
                    typeof loc.value === "string"
                      ? loc.value
                      : loc.value?.text || ""
                  }
                >
                  {loc.value}
                </Typography>
              )}
            </Box>
          </Box>
        ),
      };
    });

  return (
    <BasicModal
      open={open}
      title={title}
      handleClose={() => {
        if (pendingAppointmentModal) {
          onClose();
          return;
        }
        handleModalClose(true);
        dispatch(setSavedInfoWhenLeadCreate(null));
        clearPendingModalFlags();
        // Inline lead draft is persisted in unsavedFormData; clear global copy only
        clearInlineLeadDraft();
      }}
      size={size}
      saveButtonTitle={saveButtonTitle}
      saveBtnDisabled={requestType === "update" ? false : isSlotDisabled}
      saveCallback={() => handleAppointmentSave()}
      saveBtnLoading={loading}
      saveBtnStyles={{ padding: "2px 10px" }}
      cancelButtonTitle={getSecondaryBtnText()}
      onCancel={handleStatus}
      cancelButtonLoading={statusLoading}
      cancelBtnStyles={{ padding: "2px 10px" }}
      modalBodySx={{ padding: "1.5rem", minWidth: "300px" }}
      onEnter={onOpenModal}
      onExit={() => {
        if (!pendingAppointmentModal) {
          setShowNewLeadForm(false);
        }
      }}
      // customFooterButtons={<CustomFooterButtons />  }
    >
      {calendarLoading ? (
        <Box sx={{ display: "grid", gap: 4 }}>
          <GeneralSkeleton sx={{ transform: "scale(1)", height: "34px" }} />
          <GeneralSkeleton sx={{ transform: "scale(1)", height: "34px" }} />
          <GeneralSkeleton sx={{ transform: "scale(1)", height: "34px" }} />
          <GeneralSkeleton sx={{ transform: "scale(1)", height: "34px" }} />
          <GeneralSkeleton
            sx={{ transform: "scale(1)", height: "34px", width: "40%" }}
          />
          <GeneralSkeleton sx={{ transform: "scale(1)", height: "34px" }} />

          <Box sx={{ display: "flex", gap: "30px" }}>
            <GeneralSkeleton
              sx={{
                transform: "scale(1)",
                height: "34px",
                width: "calc(50% - 15px)",
                flex: "0 0 calc(50% - 15px)",
              }}
            />
            <GeneralSkeleton
              sx={{
                transform: "scale(1)",
                height: "34px",
                width: "calc(50% - 15px)",
                flex: "0 0 calc(50% - 15px)",
              }}
            />
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "grid", gap: 3 }}>
            <Box
              sx={{
                display: "grid",
                gap: 1,
                gridTemplateColumns: "repeat(auto-fit, minmax(100px,1fr))",
              }}
            >
              <UISelect
                name="calendarId"
                value={AppointmentForm.values.calendarId || ""}
                title="Appointment Type"
                onChange={handleCalendarChange}
                options={calendarOptions}
                clearable={false}
                // disabled={data?.id ? true : false}
                error={
                  (AppointmentForm.touched["calendarId"] &&
                    AppointmentForm.errors["calendarId"]) ||
                  false
                }
                disabled={readOnlyFields?.calendarId || inValidLead}
                showNoData={true}
                variant="outlined"
              />
              <UITextField
                type={"text"}
                name={"title"}
                value={AppointmentForm.values.title}
                title={"Appointment Title"}
                placeholder={""}
                onChange={AppointmentForm.handleChange}
                onBlur={AppointmentForm?.handleBlur}
                // tooltip={tooltip}
                // prefix={prefix}
                error={
                  (AppointmentForm.touched["title"] &&
                    AppointmentForm.errors["title"]) ||
                  false
                }
                disabled={readOnlyFields?.title || inValidLead}
              />
            </Box>
            <SearchLead
              onSelect={onChange}
              form={AppointmentForm}
              isReadOnly={readOnlyFields?.leadId || lead?.id}
              lead={lead}
              initialLead={tempLead}
              onCreateNew={
                isSchedulingUser ? undefined : handleSearchLeadCreateNew
              }
              showCreateNew={!isSchedulingUser}
            />

            {showNewLeadForm && (
              <>
                {/* <Divider sx={{ my: 2 }} /> */}
                <Box sx={{ mb: 0 }}>
                  <DynamicForm
                    formId={"new-lead-appointment-form"}
                    formJson={newLeadFormJson}
                    handleSubmit={handleNewLeadSubmit}
                    fluidView={false}
                    requiredFieldNames={["firstName", "email"]}
                    draftValues={inlineLeadDraftForForm}
                    onValuesChange={handleInlineLeadValuesChange}
                    columnWidth={280}
                    sectionStyles={{
                      gap: "16px",
                      gridTemplateColumns: "repeat(2, 1fr)",
                    }}
                  />
                  <br />
                  <Link
                    href="/crm/leads/create"
                    onClick={(e) => {
                      const inlineDraft = inlineLeadDraftRef.current;
                      const currentValues = AppointmentForm.values;
                      const draftPayload = {
                        formValues: currentValues,
                        savedLead: tempLead,
                        showInlineLeadForm: true,
                        ...(inlineDraft && { inlineLeadDraft: inlineDraft }),
                      };

                      if (lead?.id) {
                        dispatch(
                          setUnsavedLeadFormData({
                            leadId: lead.id,
                            ...draftPayload,
                          })
                        );
                      } else {
                        dispatch(setUnsavedFormData(draftPayload));
                      }

                      if (inlineDraft) {
                        dispatch(setAppointmentInlineLeadDraft(inlineDraft));
                        dispatch(
                          setDraftLeadEdit({
                            leadId: APPOINTMENT_LEAD_DRAFT_KEY,
                            data: inlineDraft,
                          })
                        );
                      }
                      dispatch(setPendingAppointmentModal(true));
                      dispatch(setAppointmentModalShowNewLeadForm(true));
                      dispatch(setSavedInfoWhenLeadCreate(currentValues));
                      e.preventDefault();
                      router.push(`/crm/leads/create?returnTo=appointment`);
                    }}
                    sx={{
                      fontSize: "1rem",
                      color: "var(--palette-secondary_button_text)",
                      textDecoration: "none",
                      pt: 2,
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Add More Information
                  </Link>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mt: 1,
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <UIButton
                      title="Cancel"
                      variant="outlined"
                      disabled={isLeadSaving}
                      onClick={() => {
                        setLeadSaveLoading(Enums.RequestStatus.initial);
                        setShowNewLeadForm(false);
                        clearInlineLeadDraft();
                      }}
                      sx={{ padding: "4px 16px", fontSize: "0.8rem" }}
                    />
                    <UIButton
                      title="Save"
                      type="submit"
                      form="new-lead-appointment-form"
                      loading={isLeadSaving}
                      disabled={isLeadSaving}
                      sx={{ padding: "4px 16px", fontSize: "0.8rem" }}
                    />
                  </Box>
                </Box>
              </>
            )}

            <UITextField
              type={"text"}
              name={"notes"}
              value={AppointmentForm.values.notes}
              title={"Note to guest"}
              placeholder={""}
              onChange={AppointmentForm.handleChange}
              onBlur={AppointmentForm?.handleBlur}
              rows={3}
              multiline={true}
              // tooltip={tooltip}
              // prefix={prefix}
              error={
                (AppointmentForm.touched["notes"] &&
                  AppointmentForm.errors["notes"]) ||
                false
              }
              maxLength="500"
              disabled={readOnlyFields?.notes || inValidLead}
              tooltip="Max 500 characters allowed"
            />
          </Box>
          <Collapse in={isCollapsed}>
            <Box sx={{ display: "grid", gap: 3, mt: 3 }}>
              <SectionHeading icon={faClock} title="Date & Time" />

              <TimeZoneField
                form={AppointmentForm}
                onChange={onChange}
                timezones={timezones}
                isReadOnly={readOnlyFields?.date || inValidLead}
                fieldName="timeZone"
              />
              <Box>
                <DateFields
                  handleSlotDisable={setIsSlotDisabled}
                  form={AppointmentForm}
                  onChange={onChange}
                  timezones={timezones}
                  isCalendarDisabled={isCalendarDisabled}
                  isReadOnly={readOnlyFields?.date || inValidLead}
                  isRerendering={isRerendering}
                  offsetMinutes={offsetMinutes}
                />
              </Box>
              {shouldShowPublicLocationSelector(
                selectedCalendar?.appointmentLocations
              ) && (
                <>
                  <SectionHeading
                    icon={faLocationDot}
                    title="Meeting Location"
                    styles={{ padding: "10px" }}
                  />
                  <LocationFields
                    form={AppointmentForm}
                    predefinedLocations={predefinedLocations}
                    appointmentLocations={
                      selectedCalendar?.appointmentLocations || []
                    }
                    onChange={onChange}
                    disabled={readOnlyFields?.calendarLocationId}
                  />
                </>
              )}
            </Box>
          </Collapse>
        </>
      )}
    </BasicModal>
  );
};

export default AppointmentModal;

const SectionHeading = ({ title, icon }) => {
  return (
    <GetIcon
      title={title}
      iconStyles={{
        marginRight: "10px",
        fontSize: "1.2rem",
        background: "var(--palette-stroke)",
        color: "var(--palette-secondary_button_text)",
        // color: "white",
        padding: "8px",
        borderRadius: "50%",
        width: 20,
        height: 20,
      }}
      sx={{
        ".MuiTypography-root": {
          fontWeight: 500,
          color: "var(--palette-text)",
        },
      }}
      icon={icon}
    />
  );
};
